import React, { useEffect, useRef } from "react";
import { Button } from "components/ui/button";
import { Loader2, Pause, Play } from "lucide-react";

const DEFAULT_AUTO_INTERVAL_MS = 8000;

export function LeaderboardPagination({
  page = 1,
  totalPages = 1,
  totalCount = 0,
  pageSize = 25,
  loading = false,
  fetching = false,
  onPageChange,
  compact = false,
  className = "",
  autoPlay = false,
  onAutoPlayChange,
  autoPlayIntervalMs = DEFAULT_AUTO_INTERVAL_MS,
  dark = false,
}) {
  const onPageChangeRef = useRef(onPageChange);
  const pageRef = useRef(page);
  const totalPagesRef = useRef(totalPages);

  onPageChangeRef.current = onPageChange;
  pageRef.current = page;
  totalPagesRef.current = totalPages;

  useEffect(() => {
    if (!autoPlay || totalPages <= 1 || fetching) return undefined;

    const timer = setInterval(() => {
      const currentPage = pageRef.current;
      const pages = totalPagesRef.current;
      if (pages <= 1) return;
      onPageChangeRef.current(currentPage >= pages ? 1 : currentPage + 1);
    }, autoPlayIntervalMs);

    return () => clearInterval(timer);
  }, [autoPlay, totalPages, autoPlayIntervalMs, fetching]);

  if (totalPages <= 1) return null;

  const from = totalCount > 0 ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(page * pageSize, totalCount);

  const barStyle = dark
    ? {
        backgroundColor: "#141a26",
        borderColor: "#2a3548",
        color: "#94a3b8",
      }
    : undefined;

  if (compact) {
    return (
      <div
        className={`shrink-0 border-t flex items-center gap-1 px-1.5 py-1 ${
          dark ? "" : "bg-white border-gray-200"
        } ${className}`}
        style={barStyle}
      >
        <Button
          variant="ghost"
          size="sm"
          className={`h-6 min-w-[40px] px-1.5 text-[10px] font-semibold ${
            dark
              ? "text-slate-300 hover:bg-slate-700 hover:text-white"
              : "text-gray-600"
          }`}
          disabled={page <= 1 || loading || fetching}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          Prev
        </Button>

        <div className="min-w-0 flex-1 flex items-center justify-center gap-1.5">
          {fetching && (
            <Loader2 className="w-3 h-3 animate-spin shrink-0 text-indigo-400" />
          )}
          <span
            className={`text-[10px] font-semibold tabular-nums truncate text-center ${
              dark ? "text-slate-300" : "text-gray-600"
            }`}
          >
            {page}/{totalPages}
            {totalCount > 0 ? (
              <span className={dark ? "text-slate-500" : "text-gray-400"}>
                {" · "}
                {from.toLocaleString("en-IN")}–{to.toLocaleString("en-IN")}
              </span>
            ) : null}
          </span>
          {onAutoPlayChange && totalPages > 1 && (
            <button
              type="button"
              onClick={() => onAutoPlayChange(!autoPlay)}
              className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0 ${
                autoPlay
                  ? dark
                    ? "bg-green-900/50 text-green-300"
                    : "bg-green-100 text-green-800"
                  : dark
                    ? "bg-slate-700 text-slate-300"
                    : "bg-gray-100 text-gray-600"
              }`}
              title={
                autoPlay
                  ? "Pause auto page rotation"
                  : `Auto-rotate pages every ${autoPlayIntervalMs / 1000}s`
              }
            >
              {autoPlay ? (
                <Pause className="w-2.5 h-2.5" />
              ) : (
                <Play className="w-2.5 h-2.5" />
              )}
              Auto
            </button>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          className={`h-6 min-w-[40px] px-1.5 text-[10px] font-semibold ${
            dark
              ? "text-slate-300 hover:bg-slate-700 hover:text-white"
              : "text-gray-600"
          }`}
          disabled={page >= totalPages || loading || fetching}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        >
          Next
        </Button>
      </div>
    );
  }

  return (
    <div
      className={`shrink-0 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-1.5 bg-white dark:bg-gray-900 px-4 py-2 ${className}`}
    >
      <Button
        variant="outline"
        size="sm"
        disabled={page <= 1 || loading || fetching}
        onClick={() => onPageChange(Math.max(1, page - 1))}
      >
        Prev
      </Button>

      <div className="flex flex-col items-center gap-1 min-w-0 flex-1">
        <span className="text-xs text-gray-500 text-center leading-tight inline-flex items-center justify-center gap-1.5">
          {fetching && <Loader2 className="w-3 h-3 animate-spin shrink-0 text-indigo-500" />}
          <span>
            Page {page} of {totalPages}
            {totalCount > 0 && (
              <>
                <br />
                <span className="text-[10px]">
                  {from.toLocaleString("en-IN")}–{to.toLocaleString("en-IN")} of{" "}
                  {totalCount.toLocaleString("en-IN")}
                </span>
              </>
            )}
          </span>
        </span>
        {onAutoPlayChange && totalPages > 1 && (
          <button
            type="button"
            onClick={() => onAutoPlayChange(!autoPlay)}
            className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full transition-colors ${
              autoPlay
                ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
                : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
            }`}
            title={
              autoPlay
                ? "Pause auto page rotation"
                : `Auto-rotate pages every ${autoPlayIntervalMs / 1000}s`
            }
          >
            {autoPlay ? (
              <>
                <Pause className="w-3 h-3" />
                Auto live
              </>
            ) : (
              <>
                <Play className="w-3 h-3" />
                Auto off
              </>
            )}
          </button>
        )}
      </div>

      <Button
        variant="outline"
        size="sm"
        disabled={page >= totalPages || loading || fetching}
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
      >
        Next
      </Button>
    </div>
  );
}

export default LeaderboardPagination;
